<template>
  <div class="w-full flex-1 flex flex-col min-h-0">
    <div class="flex items-center justify-between px-4 py-2">
      <slot name="toolbar-start" />
      <div>
        <button
          v-if="saveState === 'dirty'"
          class="flex items-center gap-1.5 text-xs text-amber-600 hover:text-amber-800 transition-colors px-2 py-1 rounded hover:bg-amber-50"
          title="Click to save now"
          @click="saveNow"
        >
          <span class="h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
          Unsaved changes
        </button>
        <span
          v-else-if="saveState === 'saving'"
          class="flex items-center gap-1.5 text-xs text-slate-500 px-2"
        >
          <svg
            class="animate-spin h-3 w-3"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          Saving…
        </span>
        <span
          v-else-if="saveState === 'synced'"
          class="flex items-center gap-1 text-xs text-green-600 px-2"
        >
          <svg
            class="h-3 w-3"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
              clip-rule="evenodd"
            />
          </svg>
          Saved
        </span>
      </div>
    </div>
    <VisualStudyDesignBuilder :mode="'edit'" v-model="localDesign" class="flex-1 min-h-0" />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { useDebounceFn } from "@vueuse/core";
import { VisualStudyDesignBuilder } from "visual-study-design-builder";
import "visual-study-design-builder/style.css";
import type { StudyDesign } from "visual-study-design-builder";
import type { ReviewItem } from "@core/domain/models/reviewItem.model";
import type { AttributeStructure } from "@core/domain/models/data-extraction-plan-attribute";
import type { StudyDesignValue } from "@core/domain/types/attributeValue.type";
import { injectStrict } from "@app/utils/injectStrict";
import { ReviewKey } from "../../../use-review";
import useSnackbar from "@app/composables/use-snackbar";
import { HttpException } from "@core/exceptions/http.exception";
import { SnackbarState } from "@app/types";
import { errorMessage } from "@app/utils/error-message";

const props = defineProps<{
  study: ReviewItem;
  attributeStructure: AttributeStructure;
  disabled?: boolean;
}>();

const review = injectStrict(ReviewKey);
const snackbar = useSnackbar();

const currentAttribute = computed(() =>
  props.study.synthesis?.attributes?.find(
    (attr) => attr.attributeStructureId === props.attributeStructure?.id,
  ),
);

const EMPTY_DESIGN: StudyDesign = {
  populations: [],
  arms: [],
  periods: [],
  interventionDefinitions: [],
};

const localDesign = ref<StudyDesign>({ ...EMPTY_DESIGN });

let skipServerLoadUntil = 0;
const SKIP_DURATION = 3000;
let isLoadingFromServer = false;

// ── save state ────────────────────────────────────────────────────────────────
const changeId = ref(0);
const savedId = ref(0);
const isSaving = ref(false);
const saveEverSucceeded = ref(false);
let saveActive = false;

type SaveState = "idle" | "dirty" | "saving" | "synced";
const saveState = computed<SaveState>(() => {
  if (isSaving.value) return "saving";
  if (changeId.value > savedId.value) return "dirty";
  if (saveEverSucceeded.value) return "synced";
  return "idle";
});

// ── load from server ──────────────────────────────────────────────────────────
function loadFromServer() {
  if (Date.now() < skipServerLoadUntil) return;

  isLoadingFromServer = true;
  const attr = currentAttribute.value;
  if (attr) {
    const value = attr.value as StudyDesignValue | undefined;
    localDesign.value = {
      populations: (value?.populations ?? []) as StudyDesign["populations"],
      arms: (value?.arms ?? []) as StudyDesign["arms"],
      periods: (value?.periods ?? []) as StudyDesign["periods"],
      interventionDefinitions: (value?.interventionDefinitions ??
        []) as StudyDesign["interventionDefinitions"],
      anchors: (value?.anchors ?? undefined) as StudyDesign["anchors"],
    };
  } else {
    localDesign.value = { ...EMPTY_DESIGN };
  }
  Promise.resolve().then(() => {
    isLoadingFromServer = false;
  });
}

watch(
  () => props.study.id,
  () => {
    skipServerLoadUntil = 0;
    changeId.value = 0;
    savedId.value = 0;
    saveEverSucceeded.value = false;
    loadFromServer();
  },
  { immediate: true },
);

watch(
  () => currentAttribute.value,
  () => {
    loadFromServer();
  },
  { deep: true },
);

// ── save ──────────────────────────────────────────────────────────────────────
async function performSave() {
  if (saveActive) return;
  if (changeId.value === savedId.value) return;
  const idAtStart = changeId.value;
  saveActive = true;
  isSaving.value = true;
  try {
    skipServerLoadUntil = Date.now() + SKIP_DURATION;
    await review.saveStudyDesignAttributeValue(
      props.study.id,
      props.attributeStructure.id,
      localDesign.value as StudyDesignValue,
    );
    skipServerLoadUntil = Date.now() + SKIP_DURATION;
    savedId.value = idAtStart;
    saveEverSucceeded.value = true;
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
  } finally {
    saveActive = false;
    isSaving.value = false;
  }
}

const debouncedPerformSave = useDebounceFn(performSave, 2000);

function syncToServer() {
  if (isLoadingFromServer) return;
  changeId.value++;
  debouncedPerformSave();
}

async function saveNow() {
  await performSave();
}

watch(localDesign, syncToServer, { deep: true });
</script>
